/**
 * Wizard Beat step (W2-08 timing; DESIGN.md §4.3 "(4) BEAT", §5.1 step 5, §5.2 in compact form). Works on the
 * `NewChartDraft` (no project exists yet): `bpm`, `offsetMs`, `timingConfirmed`.
 *
 *  - Detection started at the Song step (`draft.analysis`, W2-14) is used when present; otherwise the step runs it.
 *    The analysis asks the audio worker first (the Song step installs the draft's PCM there with `dsp.setPcm` under
 *    `music.sha256`); when the worker does not have it, the PCM comes from the audio engine (no chart open: the
 *    step loads the song there for playback) or from the draft's file, decoded by the analysis session (a chart is
 *    open and the engine keeps playing its song). While the draft still has the defaults (120 BPM, beat 0 at 0 s)
 *    and the user has not changed anything here, the detected tempo and first beat are applied, with a sentence
 *    saying so.
 *  - Waveform with the beat grid: click a downbeat to put beat 0 there, or drag the grid.
 *  - Tempo: BPM field, candidates with x2 / /2 / x1.5, a big Tap button (also the B key while the step has focus).
 *  - First beat: the sentence, -/+ (Shift 10 ms), Detect; Play with clicks over the song and Too early / Just right /
 *    Too late (Just right marks the timing as confirmed); the sentence that adjusting by ear does not depend on
 *    the headphones.
 *  - The step can always continue; skipping keeps the current (detected or default) tempo and first beat unconfirmed,
 *    and the chart reminds the user (ST-01).
 * The song plays through the app's audio engine. Without an open chart the step loads the draft's song there and
 * sets the draft's offset. With a chart open the engine keeps the chart's song and offsets: the step can play only
 * when the draft's song is that song (otherwise Play with clicks and the answers by ear are disabled with a reason),
 * and its clicks are shifted by the difference between the draft's beat 0 and the chart's, so they land where the
 * draft puts the beats. Leaving the step stops the playback it started and gives the metronome back to the editor.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import type { WizardStepProps } from '../../app/registry/types';
import { createMeterMap } from '../../core/time/meter';
import { createTempoMap } from '../../core/time/tempo';
import type { Sec } from '../../core/time/types';
import { Button, EmptyState, Icon, IconButton } from '../../ui';
import { BpmField } from './TempoPanel';
import { ByEarAnswers } from './OffsetHelper';
import { SongOverview } from './SongOverview';
import { TapTempo } from './TapTempo';
import { TempoDetect } from './TempoDetect';
import { m } from './timing.messages';
import { timingAnalysisFor, useSongAnalysis } from './timingAnalysis';
import { restoreEditorMetronome, type MetronomeBeats } from './timingDoc';
import { useMusic, useStore, useText, useTimingView, useTransport } from './timingHooks';
import {
  addTap,
  byEarDelta,
  byEarStepMs,
  cleanBpm,
  EMPTY_TAPS,
  isValidBpm,
  readTaps,
  type ByEarAnswer,
  type TapState,
} from './timingModel';
import s from './Timing.module.css';

/** Defaults the wizard starts from (DESIGN.md §4.3: "Skip -> 120 BPM / offset 0"). */
const DEFAULT_BPM = 120;

/**
 * Metronome beat times for a constant tempo with an accent every `perBar` beats; beat 0 at `shiftSec` of the
 * engine's chart time (0 when the engine's offset is the draft's own).
 */
export function constantBeatTimes(bpm: number, perBar = 4, shiftSec = 0): MetronomeBeats {
  const period = 60 / bpm;
  return (t0, t1) => {
    const out: Array<{ readonly t: Sec; readonly accent: boolean }> = [];
    if (!(t1 > t0) || !(period > 0)) return out;
    const first = Math.max(0, Math.ceil((t0 - shiftSec) / period - 1e-9));
    for (let b = first; b * period + shiftSec < t1 && out.length < 4096; b++) {
      out.push({ t: b * period + shiftSec, accent: b % perBar === 0 });
    }
    return out;
  };
}

/** Beat 0 in whole milliseconds, typed between the -/+ buttons (Enter or leaving the field commits). */
function OffsetMsInput(props: {
  readonly label: string;
  readonly unit: string;
  readonly valueMs: number;
  onCommit(ms: number): void;
}) {
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const commit = (): void => {
    if (draft === null) return;
    const v = Number(draft.trim().replace(',', '.'));
    if (Number.isFinite(v) && Math.round(v) !== props.valueMs) props.onCommit(Math.round(v));
    setDraft(null);
  };
  return (
    <>
      <label className="visually-hidden" htmlFor={id}>
        {props.label}
      </label>
      <input
        id={id}
        className={s.input}
        style={{ width: '5.5em' }}
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        value={draft ?? String(Math.round(props.valueMs))}
        data-testid="timing-step-offset-ms"
        onChange={(e) => {
          setDraft(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape' && draft !== null) {
            e.preventDefault();
            e.stopPropagation();
            setDraft(null);
          }
        }}
        onBlur={commit}
      />
      <span className={s.subtle}>{props.unit}</span>
    </>
  );
}

export function BeatStep(props: WizardStepProps) {
  const { services, draft } = props;
  const propsRef = useRef(props);
  useEffect(() => {
    propsRef.current = props;
  });
  const text = useText(services);
  const music = draft.music;
  const audio = music?.sha256 ?? null;
  const analysis = useSongAnalysis(services, audio);
  const transport = useTransport(services);
  const [taps, setTaps] = useState<TapState>(EMPTY_TAPS);
  const [byEar, setByEar] = useState(0);
  const [clicks, setClicks] = useState(false);
  const [detectedNote, setDetectedNote] = useState<{ readonly bpm: number; readonly offsetMs: number } | null>(null);
  const [offsetBusy, setOffsetBusy] = useState(false);
  const [redetected, setRedetected] = useState(false);
  const touched = useRef(false);
  const autoApplied = useRef(false);
  /** The step started the playback that is running (stopped when the step goes away). */
  const startedPlayback = useRef(false);
  /** The step's clicks are installed in the engine's metronome (the editor's are put back when it goes away). */
  const ownClicks = useRef(false);
  const dragBase = useRef<number | null>(null);
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  });

  useEffect(() => {
    propsRef.current.setCanContinue(true);
  }, []);

  // The step plays the draft's song through the app's engine only while no chart is open (the wizard runs from
  // the Library); with a chart open its song and offsets stay untouched and the step analyses without playback,
  // unless the draft's song is the chart's song: then it plays it with the chart's offsets and shifts its clicks.
  const store = useStore(services);
  const openView = useTimingView(store);
  const engineMusic = useMusic(services);
  const engineFree = store === null;
  const canListen = engineFree || engineMusic?.asset === audio;
  /** Music time (s) of the engine's chart time 0: the draft's beat 0 when the step sets the offset. */
  const engineShiftSec = engineFree || openView === null ? draft.offsetMs / 1000 : openView.shiftSec;
  /** The draft's beat 0 in the engine's chart time. */
  const clickShiftSec = draft.offsetMs / 1000 - engineShiftSec;
  const live = useRef({ engineFree, canListen, engineShiftSec, clickShiftSec });
  useEffect(() => {
    live.current = { engineFree, canListen, engineShiftSec, clickShiftSec };
  });

  // The draft's file, for the analysis when neither the worker nor the engine has the song.
  useEffect(() => {
    if (music === null || audio === null) return;
    return timingAnalysisFor(services).provideFile(audio, music.bytes);
  }, [services, music, audio]);

  // Decode the song into the engine when it is free (playback, and the PCM for the analysis), then analyse it.
  useEffect(() => {
    if (music === null || audio === null) return;
    const analysis = timingAnalysisFor(services);
    if (engineFree && services.audio.music?.asset !== audio) {
      void analysis.loadSong(audio, music.bytes).catch(() => undefined);
    }
    analysis.ensure(audio);
  }, [services, music, audio, engineFree]);

  // The Song step's candidates until the user detects again here; then the step's own result.
  const candidates =
    (redetected ? analysis?.candidates : null) ?? draft.analysis?.candidates ?? analysis?.candidates ?? null;

  // Detect the tempo when the Song step did not.
  useEffect(() => {
    if (audio === null || candidates !== null || analysis === null) return;
    if (analysis.detecting || analysis.error !== null) return;
    void timingAnalysisFor(services)
      .detectTempo(audio)
      .catch(() => undefined);
  }, [services, audio, candidates, analysis]);

  // Apply the detection while the draft still has the defaults.
  useEffect(() => {
    const top = candidates?.[0];
    if (audio === null || top === undefined || autoApplied.current || touched.current) return;
    const d = draftRef.current;
    if (d.timingConfirmed || d.bpm !== DEFAULT_BPM || d.offsetMs !== 0) return;
    autoApplied.current = true;
    const bpm = top.bpm;
    const fromSongStep = draft.analysis?.offset ?? null;
    const apply = (offsetMs: number): void => {
      if (touched.current) return;
      propsRef.current.update({ bpm, offsetMs });
      setDetectedNote({ bpm, offsetMs });
    };
    if (fromSongStep !== null && fromSongStep.confidence > 0 && draft.analysis?.candidates?.[0]?.bpm === bpm) {
      apply(fromSongStep.offsetMs);
      return;
    }
    void timingAnalysisFor(services)
      .suggestOffset(audio, [{ beat: 0, bpm }], 0)
      .then((r) => {
        apply(r !== null && r.confidence > 0 ? r.offsetMs : 0);
      })
      .catch(() => {
        apply(0);
      });
  }, [services, audio, candidates, draft.analysis]);

  // Playback follows the draft: chart offset and metronome clicks.
  useEffect(() => {
    if (engineFree) services.audio.setOffset(draft.offsetMs, 0, 0);
  }, [services, draft.offsetMs, engineFree]);
  useEffect(() => {
    if (!clicks) return;
    ownClicks.current = true;
    services.audio.setMetronome(true, constantBeatTimes(draft.bpm, 4, clickShiftSec));
  }, [services, clicks, draft.bpm, clickShiftSec]);

  // Playback that ends by itself (end of the song, another control) ends the clicks too.
  const wasPlaying = useRef(transport.playing);
  useEffect(() => {
    const was = wasPlaying.current;
    wasPlaying.current = transport.playing;
    if (!was || transport.playing || !startedPlayback.current) return;
    startedPlayback.current = false;
    setClicks(false);
    if (ownClicks.current) {
      ownClicks.current = false;
      restoreEditorMetronome(services);
    }
  }, [services, transport.playing]);

  // Leaving the step: stop the playback it started (any playback of the draft's song when the engine was free)
  // and give the metronome back to the editor.
  useEffect(
    () => () => {
      const { engineFree: free } = live.current;
      const tr = services.audio.transport;
      if (tr.state.playing && (free || startedPlayback.current)) tr.stop(free ? {} : { returnToStart: true });
      startedPlayback.current = false;
      if (free || ownClicks.current) restoreEditorMetronome(services);
      ownClicks.current = false;
    },
    [services],
  );

  const edit = useCallback((patch: { bpm?: number; offsetMs?: number; timingConfirmed?: boolean }): void => {
    touched.current = true;
    setDetectedNote(null);
    propsRef.current.update(patch);
  }, []);

  const tempo = useMemo(() => createTempoMap([{ bpm: draft.bpm, startTime: [0, 0, 1] }]), [draft.bpm]);
  const meter = useMemo(() => createMeterMap([]), []);
  const reading = readTaps(taps);

  const tap = (): void => {
    setTaps((t) => addTap(t, performance.now()));
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    if (e.code !== 'KeyB' || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
    if (target.closest('input, textarea, [contenteditable="true"]') !== null) return;
    e.preventDefault();
    tap();
  };

  const toggleClicks = async (): Promise<void> => {
    const tr = services.audio.transport;
    const { engineFree: free, canListen: listen, clickShiftSec: shift } = live.current;
    if (clicks && tr.state.playing) {
      startedPlayback.current = false;
      tr.stop(free ? {} : { returnToStart: true });
      setClicks(false);
      ownClicks.current = false;
      restoreEditorMetronome(services);
      return;
    }
    if (!listen) return;
    try {
      await services.audio.ensureContext();
      ownClicks.current = true;
      services.audio.setMetronome(true, constantBeatTimes(draftRef.current.bpm, 4, shift));
      setClicks(true);
      startedPlayback.current = true;
      await tr.play({ mode: 'flow' });
    } catch {
      startedPlayback.current = false;
      setClicks(false);
      ownClicks.current = false;
      restoreEditorMetronome(services);
    }
  };

  const answer = (a: ByEarAnswer): void => {
    const { deltaMs, next } = byEarDelta(a, byEar);
    setByEar(next);
    if (a === 'justRight') edit({ timingConfirmed: true });
    else edit({ offsetMs: Math.round(draftRef.current.offsetMs + deltaMs) });
  };

  const detectOffset = (): void => {
    if (audio === null) return;
    setOffsetBusy(true);
    void timingAnalysisFor(services)
      .suggestOffset(audio, [{ beat: 0, bpm: draft.bpm }], draft.offsetMs)
      .then((r) => {
        if (r !== null && r.confidence > 0) edit({ offsetMs: r.offsetMs });
        else services.ui.toast({ key: m.offsetNotFound }, { kind: 'warning' });
      })
      .catch(() => {
        services.ui.toast({ key: m.offsetNotFound }, { kind: 'warning' });
      })
      .finally(() => {
        setOffsetBusy(false);
      });
  };

  if (music === null || audio === null) {
    return (
      <EmptyState
        icon="Music"
        title={{ key: m.stepNoSong }}
        body={{ key: m.stepSkipNote, params: { bpm: draft.bpm, sec: draft.offsetMs / 1000 } }}
      />
    );
  }

  const duration = analysis?.durationSec ?? music.durationSec;
  return (
    <div className={s.step} onKeyDown={onKeyDown} data-testid="timing-beat-step">
      <p className={s.help}>{text.t(m.stepIntro)}</p>
      {detectedNote !== null ? (
        <div className={s.notice} data-kind="ok" role="status">
          <Icon name="AudioWaveform" size={16} />
          <span>{text.t(m.stepDetected, { bpm: detectedNote.bpm, sec: detectedNote.offsetMs / 1000 })}</span>
        </div>
      ) : (
        candidates === null && (
          <div className={s.notice} role="status">
            <Icon name="AudioWaveform" size={16} />
            <span>{text.t(m.stepDetecting)}</span>
          </div>
        )
      )}
      <div className={s.stepOverview}>
        <SongOverview
          text={text}
          scheduler={services.scheduler}
          id="timing.beatStep.overview"
          label={text.t(m.stepClickHint)}
          peaks={analysis?.peaks ?? null}
          onsets={analysis?.onsets ?? null}
          sampleRate={analysis?.sampleRate ?? null}
          durationSec={duration}
          tempo={tempo}
          meter={meter}
          shiftSec={draft.offsetMs / 1000}
          playheadMusicSec={() => (services.audio.music?.asset === audio ? services.audio.transport.musicTime() : null)}
          playing={transport.playing}
          onSeek={(sec) => {
            // Seek only the song the step can play (never the open chart's other song).
            if (live.current.canListen) services.audio.transport.seek(sec - live.current.engineShiftSec);
          }}
          onBeatZero={(sec) => {
            edit({ offsetMs: Math.round(sec * 1000) });
          }}
          onDrag={(delta, phase) => {
            if (dragBase.current === null) dragBase.current = draftRef.current.offsetMs;
            const base = dragBase.current;
            if (phase === 'cancel') edit({ offsetMs: base });
            else edit({ offsetMs: Math.round(base + delta * 1000) });
            if (phase !== 'move') dragBase.current = null;
          }}
        />
      </div>
      <div className={s.stepColumns}>
        <section className={s.stack} aria-label={text.t(m.sectionTempo)}>
          <BpmField
            text={text}
            value={draft.bpm}
            onCommit={(bpm) => {
              if (isValidBpm(bpm)) edit({ bpm: cleanBpm(bpm) });
            }}
          />
          <TempoDetect
            text={text}
            candidates={candidates}
            detecting={analysis?.detecting === true}
            progress={analysis?.task === 'tempo' ? analysis.progress : null}
            error={analysis?.error ?? null}
            currentBpm={draft.bpm}
            onDetect={() => {
              setRedetected(true);
              void timingAnalysisFor(services)
                .detectTempo(audio, { force: candidates !== null })
                .catch(() => undefined);
            }}
            onUse={(bpm) => {
              edit({ bpm });
            }}
            onMultiply={(factor) => {
              const next = cleanBpm(draft.bpm * factor);
              if (isValidBpm(next)) edit({ bpm: next });
            }}
          />
          <TapTempo
            text={text}
            reading={reading}
            chord="KeyB"
            onTap={tap}
            onUse={(bpm) => {
              edit({ bpm });
              setTaps(EMPTY_TAPS);
            }}
            onReset={() => {
              setTaps(EMPTY_TAPS);
            }}
          />
        </section>
        <section className={s.stack} aria-label={text.t(m.sectionFirstBeat)}>
          <p className={s.sentence} data-testid="timing-step-beat-zero">
            {text.t(m.beatZeroAt, { sec: draft.offsetMs / 1000 })}
          </p>
          <div className={s.rowTight}>
            <IconButton
              icon="Minus"
              size="sm"
              label={{ key: m.offsetEarlier }}
              onClick={(e) => {
                edit({ offsetMs: draft.offsetMs - (e.shiftKey ? 10 : 1) });
              }}
            />
            <OffsetMsInput
              label={text.t(m.stepOffsetLabel)}
              unit={text.t(m.offsetUnit)}
              valueMs={draft.offsetMs}
              onCommit={(ms) => {
                edit({ offsetMs: ms });
              }}
            />
            <IconButton
              icon="Plus"
              size="sm"
              label={{ key: m.offsetLater }}
              onClick={(e) => {
                edit({ offsetMs: draft.offsetMs + (e.shiftKey ? 10 : 1) });
              }}
            />
            <Button
              size="sm"
              icon="Crosshair"
              onClick={detectOffset}
              disabledReason={offsetBusy ? { key: m.detecting } : null}
              data-testid="timing-step-detect-offset"
            >
              {text.t(m.detect)}
            </Button>
          </div>
          <div className={s.row}>
            <Button
              icon={clicks && transport.playing ? 'Square' : 'Play'}
              variant="primary"
              disabledReason={canListen ? null : { key: m.stepEngineBusy }}
              onClick={() => {
                void toggleClicks();
              }}
              data-testid="timing-step-play"
            >
              {text.t(clicks && transport.playing ? m.stopClicks : m.playWithClicks)}
            </Button>
          </div>
          <ByEarAnswers
            text={text}
            disabledReason={canListen ? null : m.stepEngineBusy}
            stepMs={byEarStepMs(byEar)}
            canReset={byEar > 0}
            confirmed={draft.timingConfirmed}
            onAnswer={answer}
            onReset={() => {
              setByEar(0);
            }}
          />
          <p className={s.row} style={{ margin: 0 }}>
            <Icon name={draft.timingConfirmed ? 'CircleCheck' : 'CircleAlert'} size={14} />
            <span className={s.muted}>{text.t(draft.timingConfirmed ? m.confirmedYes : m.confirmedNo)}</span>
          </p>
          <p className={s.inlineNote}>
            <Icon name="Headphones" size={14} />
            <span>{text.t(m.byEarHeadphones)}</span>
          </p>
          {!draft.timingConfirmed && (
            <p className={s.subtle}>{text.t(m.stepSkipNote, { bpm: draft.bpm, sec: draft.offsetMs / 1000 })}</p>
          )}
        </section>
      </div>
    </div>
  );
}
